import React from 'react'
import { Box, Typography, Card, CardContent, Divider, CircularProgress } from '@mui/material'

const MobileDataRow = ({ label, value, unit }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{value}</Typography>
            {unit && <Typography variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>{unit}</Typography>}
        </Box>
    </Box>
)

const MobileDeviceCard = ({ title, subtitle, status, statusIcon, children }) => (
    <Card sx={{ mb: 2, borderRadius: '12px', boxShadow: 'none', border: '1px solid #eee' }}>
        <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333' }}>{title}</Typography>
                    <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <img src={statusIcon} alt="status" style={{ width: '12px', marginRight: '6px' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: status === 'ONLINE' ? '#4caf50' : '#f44336' }}>{status}</Typography>
                </Box>
            </Box>
            <Divider sx={{ mb: 1.5, opacity: 0.5 }} />
            {children}
        </CardContent>
    </Card>
)

const LiveDataViewMobileView = ({ logic }) => {
    const { fetchedData, calculatedData, calculatedInverters, availableDevices, loading } = logic

    if (loading && !fetchedData.inverters.length) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
    }

    return (
        <Box>
            <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mb: 2 }}>Real-Time Device Readings</Typography>

            {fetchedData.inverters.map(inv => (
                <MobileDeviceCard key={inv.inverterID} title={inv.inverterID} subtitle={inv.name} status={inv.status} statusIcon={inv.statusIcon}>
                    <MobileDataRow
                        label={inv.power.gapDays > 1 ? `Power (Since ${inv.power.gapDays}d)` : "Power Output"}
                        value={inv.power.value} unit={inv.power.unit}
                    />
                    <MobileDataRow
                        label={inv.todayGeneration.gapDays > 1 ? `Today Yield (Since ${inv.todayGeneration.gapDays}d)` : "Today's Yield"}
                        value={inv.todayGeneration.value} unit={inv.todayGeneration.unit}
                    />
                    {inv.model && <MobileDataRow label="Model" value={inv.model} />}
                </MobileDeviceCard>
            ))}

            {availableDevices.sensors > 0 && (
                <MobileDeviceCard title={fetchedData.sensor.sensorID} subtitle={fetchedData.sensor.name} status={fetchedData.sensor.status} statusIcon={fetchedData.sensor.statusIcon}>
                    <MobileDataRow
                        label={fetchedData.sensor.temperature.gapDays > 1 ? `Temp. (Since ${fetchedData.sensor.temperature.gapDays}d)` : "Temperature"}
                        value={fetchedData.sensor.temperature.value} unit={fetchedData.sensor.temperature.unit}
                    />
                    <MobileDataRow
                        label={fetchedData.sensor.irradiance.gapDays > 1 ? `Irr. (Since ${fetchedData.sensor.irradiance.gapDays}d)` : "Irradiance"}
                        value={fetchedData.sensor.irradiance.value} unit={fetchedData.sensor.irradiance.unit}
                    />
                    <MobileDataRow
                        label={fetchedData.sensor.irradiation.gapDays > 1 ? `Ins. (Since ${fetchedData.sensor.irradiation.gapDays}d)` : "Insulation"}
                        value={fetchedData.sensor.irradiation.value} unit={fetchedData.sensor.irradiation.unit}
                    />
                </MobileDeviceCard>
            )}

            <Typography variant="overline" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', mt: 4, mb: 2 }}>Calculated Comparison</Typography>

            {availableDevices.energyMeters > 0 && (
                <MobileDeviceCard title={calculatedData.meter.meterID} subtitle={calculatedData.meter.name} status={calculatedData.meter.status} statusIcon={calculatedData.meter.statusIcon}>
                    <MobileDataRow
                        label={calculatedData.meter.lastEnergyExport.gapDays > 1
                            ? `Prev. Total (Since ${calculatedData.meter.lastEnergyExport.gapDays}d)`
                            : "Prev. Total Energy"}
                        value={calculatedData.meter.lastEnergyExport.value}
                        unit={calculatedData.meter.lastEnergyExport.unit}
                    />
                    <MobileDataRow label="Curr. Total Energy" value={calculatedData.meter.current.value} unit={calculatedData.meter.current.unit} />
                    <Divider sx={{ my: 1, opacity: 0.3 }} />
                    <MobileDataRow label="Today's Export" value={calculatedData.meter.todayGeneration.value} unit={calculatedData.meter.todayGeneration.unit} />
                </MobileDeviceCard>
            )}

            {calculatedInverters.length > 0 ? (
                calculatedInverters.map(inv => (
                    <MobileDeviceCard key={inv.inverterID} title={inv.inverterID} subtitle={inv.name} status={inv.status} statusIcon={inv.statusIcon}>
                        <MobileDataRow
                            label={inv.last.gapDays > 1
                                ? `Prev. Energy (Since ${inv.last.gapDays}d)`
                                : "Prev. Energy"}
                            value={inv.last.value}
                            unit={inv.last.unit}
                        />
                        <MobileDataRow label="Curr. Energy" value={inv.current.value} unit={inv.current.unit} />
                        <Divider sx={{ my: 1, opacity: 0.3 }} />
                        <MobileDataRow label="Generation" value={inv.todayGeneration.value} unit={inv.todayGeneration.unit} />
                    </MobileDeviceCard>
                ))
            ) : (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">No inverter data available</Typography>
                </Box>
            )}
        </Box>
    )
}

export default LiveDataViewMobileView
